// SpaceCard.jsx — PRODUCTION v2
// Place at: src/components/SpaceCard.jsx
//
// ADDED: top post preview card showing most liked post from the space

import { useState, useCallback } from "react";
import { joinSpace } from "../lib/spaceQueries";

export default function SpaceCard({ space, currentUser, onOpenSpace }) {
  const [joining, setJoining] = useState(false);
  const [joined,  setJoined]  = useState(false);
  const [error,   setError]   = useState(null);

  const isMember = space.user_role && space.user_role !== "none";
  const label    = space.reason === "most_members" ? "🔥 Trending Space" : "⭐ Featured Space";

  const handleJoin = useCallback(async (e) => {
    e.stopPropagation();
    if (!currentUser) return;
    setJoining(true); setError(null);
    try {
      await joinSpace(space.id, currentUser.id);
      setJoined(true);
    } catch (err) {
      setError(err.message?.includes("banned") ? "You've been removed from this space." : "Failed to join. Try again.");
    } finally {
      setJoining(false);
    }
  }, [space.id, currentUser]);

  const handleOpen = useCallback(() => { onOpenSpace?.(space); }, [space, onOpenSpace]);

  return (
    <div style={{
      borderRadius: 16,
      border: `1.5px solid ${space.color || "var(--primary-color)"}30`,
      background: `linear-gradient(135deg, ${space.color || "#6366f1"}08, var(--card-bg-color))`,
      marginBottom: 16, overflow: "hidden",
      transition: "transform 0.15s, box-shadow 0.15s",
      position: "relative",
    }}
      onMouseEnter={e => { e.currentTarget.style.transform="translateY(-2px)"; e.currentTarget.style.boxShadow=`0 8px 24px ${space.color||"#6366f1"}20`; }}
      onMouseLeave={e => { e.currentTarget.style.transform="none"; e.currentTarget.style.boxShadow="none"; }}
    >
      {/* Decorative circle */}
      <div style={{ position:"absolute", top:-20, right:-20, width:100, height:100, borderRadius:"50%", background:`${space.color||"#6366f1"}12`, pointerEvents:"none" }} />

      <div style={{ padding: "20px 22px 16px" }}>
        {/* Label */}
        <div style={{ fontSize:11, fontWeight:700, color: space.color||"var(--primary-color)", letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:10 }}>{label}</div>

        {/* Space header */}
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
          <div style={{ width:44, height:44, borderRadius:12, background:`${space.color||"#6366f1"}18`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, flexShrink:0, border:`1.5px solid ${space.color||"#6366f1"}30` }}>
            {space.avatar_emoji || "🏡"}
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontWeight:700, fontSize:"0.95rem", color:"var(--text-color)", marginBottom:2, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{space.name}</div>
            <div style={{ fontSize:"0.75rem", color:"var(--text-muted)" }}>🛡️ by @{space.moderator_username}</div>
          </div>
          <div style={{ fontSize:"0.75rem", color:"var(--text-muted)", textAlign:"right", flexShrink:0 }}>
            <div style={{ fontWeight:700, fontSize:"0.9rem", color:"var(--text-color)" }}>{space.member_count?.toLocaleString()}</div>
            <div>members</div>
          </div>
        </div>

        {/* Weekly prompt */}
        {space.weekly_prompt && (
          <div style={{ background:`${space.color||"#6366f1"}10`, border:`1px solid ${space.color||"#6366f1"}20`, borderRadius:10, padding:"10px 14px", marginBottom:14 }}>
            <div style={{ fontSize:"0.72rem", fontWeight:700, color: space.color||"var(--primary-color)", marginBottom:4, textTransform:"uppercase", letterSpacing:"0.05em" }}>💬 This Week's Question</div>
            <div style={{ fontSize:"0.875rem", color:"var(--text-color)", lineHeight:1.5, fontStyle:"italic" }}>"{space.weekly_prompt}"</div>
          </div>
        )}

        {/* Top post preview — only shown when space has an active top post */}
        {space.top_post_content && (
          <div
            onClick={handleOpen}
            style={{
              background: "var(--card-bg-color)",
              border: `1px solid ${space.color||"#6366f1"}25`,
              borderRadius: 12, padding: "12px 14px", marginBottom: 14,
              cursor: "pointer", transition: "background 0.12s",
            }}
            onMouseEnter={e => e.currentTarget.style.background=`${space.color||"#6366f1"}08`}
            onMouseLeave={e => e.currentTarget.style.background="var(--card-bg-color)"}
          >
            <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
              <div style={{
                width:22, height:22, borderRadius:"50%",
                background:`${space.color||"#6366f1"}20`,
                color: space.color||"var(--primary-color)",
                display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:"0.65rem", fontWeight:800, flexShrink:0,
              }}>
                {space.top_post_author?.[0]?.toUpperCase()}
              </div>
              <span style={{ fontSize:"0.72rem", fontWeight:700, color:"var(--text-muted)" }}>
                @{space.top_post_author}
              </span>
              {space.top_post_likes_this_week > 0 && (
                <span style={{
                  marginLeft:"auto", fontSize:"0.7rem", fontWeight:700,
                  color: space.color||"var(--primary-color)",
                  background:`${space.color||"#6366f1"}15`,
                  padding:"1px 7px", borderRadius:20,
                }}>
                  ❤️ {space.top_post_likes_this_week}
                </span>
              )}
            </div>
            <p style={{
              margin:0, fontSize:"0.875rem", color:"var(--text-color)",
              lineHeight:1.6, display:"-webkit-box",
              WebkitLineClamp:3, WebkitBoxOrient:"vertical", overflow:"hidden",
            }}>
              {space.top_post_content}{space.top_post_content?.length >= 120 ? "…" : ""}
            </p>
          </div>
        )}

        {/* Stats row */}
        <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:14, fontSize:"0.78rem", color:"var(--text-muted)" }}>
          {space.reason === "most_members" && space.new_members_this_week > 0 && (
            <span>🚀 +{space.new_members_this_week} joined this week</span>
          )}
          {space.reason === "most_liked" && space.top_post_likes_this_week > 0 && (
            <span>❤️ {space.top_post_likes_this_week} likes on top post</span>
          )}
          {space.tag && (
            <span style={{ background:`${space.color||"#6366f1"}15`, color: space.color||"var(--primary-color)", padding:"2px 8px", borderRadius:20, fontWeight:600 }}>
              #{space.tag}
            </span>
          )}
        </div>

        {error && <div style={{ fontSize:"0.78rem", color:"#ef4444", marginBottom:8 }}>{error}</div>}

        {/* Action buttons */}
        <div style={{ display:"flex", gap:8 }}>
          {!isMember && !joined ? (
            <button onClick={handleJoin} disabled={joining || !currentUser} style={{
              flex:1, padding:"9px 0", borderRadius:10, border:"none",
              background: space.color||"var(--primary-color)", color:"#fff",
              fontWeight:700, fontSize:"0.85rem",
              cursor: joining ? "not-allowed" : "pointer",
              opacity: joining ? 0.7 : 1, transition:"opacity 0.15s", fontFamily:"inherit",
            }}>
              {joining ? "Joining…" : space.weekly_prompt ? "Join & Answer →" : "Join Space →"}
            </button>
          ) : (
            <button onClick={handleOpen} style={{
              flex:1, padding:"9px 0", borderRadius:10, border:"none",
              background: space.color||"var(--primary-color)", color:"#fff",
              fontWeight:700, fontSize:"0.85rem", cursor:"pointer", fontFamily:"inherit",
            }}>
              {space.weekly_prompt ? "Answer →" : "View Space →"}
            </button>
          )}
          <button onClick={handleOpen} style={{
            padding:"9px 16px", borderRadius:10,
            border:`1.5px solid ${space.color||"var(--primary-color)"}40`,
            background:"transparent", color: space.color||"var(--primary-color)",
            fontWeight:600, fontSize:"0.85rem", cursor:"pointer",
            fontFamily:"inherit", transition:"background 0.15s",
          }}
            onMouseEnter={e => e.currentTarget.style.background=`${space.color||"#6366f1"}10`}
            onMouseLeave={e => e.currentTarget.style.background="transparent"}
          >
            View Space
          </button>
        </div>
      </div>
    </div>
  );
}